import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useEffect } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Spacer, FieldSet, Flex, Label, Popper, FormHelp, AdornedInput, Adornment, Input, Popover, Heading, Paragraph, Anchor } from '@zillow/constellation';
import { FieldConfig, roundToNearestInteger, isSameFloat, roundToTheNearestTenth, validateAndTrackOptionsBlock, DISCOUNT_POINTS } from './ClosingCostCalculator.utils.js';
import { getCommaSeparatedNumber, commaSeparateNumber } from '../../utils/zhl-calculator.js';
import useValidFieldUpdate from '../../utils/form/useValidFieldUpdate.js';

var DownPaymentField = function DownPaymentField() {
  var _proxyFormState$error, _proxyFormState$error2;
  var form = useFormContext();
  var proxyFormState = useFormState();
  var error = (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error = proxyFormState.errors[FieldConfig.downPayment.name]) === null || _proxyFormState$error === void 0 ? void 0 : _proxyFormState$error.message) || (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error2 = proxyFormState.errors[FieldConfig.downPaymentPercent.name]) === null || _proxyFormState$error2 === void 0 ? void 0 : _proxyFormState$error2.message);
  var fieldAmount = form.register(FieldConfig.downPayment.name, FieldConfig.downPayment.config);
  var fieldPercent = form.register(FieldConfig.downPaymentPercent.name, FieldConfig.downPaymentPercent.config);
  var amountInputRef = useRef(null);
  var formatAmount = commaSeparateNumber;

  // Format on mount
  useEffect(function () {
    if (formatAmount) {
      var defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  var trigger = /*#__PURE__*/React.createElement(Popover, {
    header: /*#__PURE__*/React.createElement(Heading, {
      level: 6
    }, "Down payment"),
    body: /*#__PURE__*/React.createElement(Paragraph, null, "A", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillow.com/learn/down-payment-on-a-house/",
      target: "_blank"
    }, "down payment"), ' ', "is the cash payment you make at closing toward the purchase of a home. Most loans require a down payment of at least 3% of the home purchase price. While not required, putting 20% down may help you avoid private mortgage insurance (PMI).", ' ', /*#__PURE__*/React.createElement(Anchor, {
      href: "https://www.zillow.com/learn/private-mortgage-insurance/",
      target: "_blank"
    }, "Learn more"))
  });
  useSyncDownPaymentPercent();
  return /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(FieldSet, {
    legend: /*#__PURE__*/React.createElement(Flex, {
      display: "flex",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Label, {
      htmlFor: "downPaymentAmount"
    }, "Down payment"), /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(Popper, {
      triggered: trigger
    }))),
    description:
    /*#__PURE__*/
    // Use blank space when no error or description to avoid
    // layout shift when when error is shown.
    React.createElement(FormHelp, {
      error: Boolean(error)
    }, error || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")),
    required: true
  }, /*#__PURE__*/React.createElement(Flex, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '1'
    },
    error: Boolean(error),
    leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
    input: /*#__PURE__*/React.createElement(Input, _extends({
      style: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRightWidth: 0
      },
      id: "downPaymentAmount"
    }, fieldAmount, {
      ref: function ref(el) {
        amountInputRef.current = el;
        fieldAmount.ref(el);
      },
      onInput: formatAmount ? function (e) {
        e.target.value = formatAmount(e.target.value);
      } : undefined,
      autoComplete: "off"
    }))
  }), /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '0.6'
    },
    error: Boolean(error),
    rightAdornment: /*#__PURE__*/React.createElement(Adornment, null, "%"),
    input: /*#__PURE__*/React.createElement(Input, _extends({}, fieldPercent, {
      autoComplete: "off",
      style: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      }
    }))
  }))));
};
function useSyncDownPaymentPercent() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  var updateDiscountPoint = function updateDiscountPoint() {
    if (lastValidFields.current[FieldConfig.purchasingAnyDiscountPoints.name] == DISCOUNT_POINTS.oneDiscountPoint) {
      var nextPurchasingAnyDiscountPoints = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.01;
      form.setValue(FieldConfig.discountPoints.name, getCommaSeparatedNumber(roundToNearestInteger(nextPurchasingAnyDiscountPoints)), validateAndTrackOptionsBlock);
    } else if (lastValidFields.current[FieldConfig.purchasingAnyDiscountPoints.name] == DISCOUNT_POINTS.twoDiscountPoints) {
      var _nextPurchasingAnyDiscountPoints = 2 * (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.01;
      form.setValue(FieldConfig.discountPoints.name, getCommaSeparatedNumber(roundToNearestInteger(_nextPurchasingAnyDiscountPoints)), validateAndTrackOptionsBlock);
    } else {
      var _nextPurchasingAnyDiscountPoints2 = 0;
      form.setValue(FieldConfig.discountPoints.name, _nextPurchasingAnyDiscountPoints2, validateAndTrackOptionsBlock);
    }
  };
  useValidFieldUpdate(form, function (name, value) {
    var _Object$assign;
    lastValidFields.current = Object.assign({}, lastValidFields.current, (_Object$assign = {}, _Object$assign[name] = value, _Object$assign));

    // 1) on change of downPayment, update downPaymentPercent based  on homeprice
    if (name === FieldConfig.downPayment.name) {
      var nextDownPaymenPercent = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      var nextLoanOriginationFee = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.5 / 100;
      var nextPrepaidInterest = roundToNearestInteger((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      if (!isSameFloat(nextDownPaymenPercent, lastValidFields.current.downPaymentPercent)) {
        form.setValue(FieldConfig.downPaymentPercent.name, roundToTheNearestTenth(nextDownPaymenPercent), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextLoanOriginationFee, lastValidFields.current.loanOriginationFee)) {
        form.setValue(FieldConfig.loanOriginationFee.name, getCommaSeparatedNumber(roundToNearestInteger(nextLoanOriginationFee)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextPrepaidInterest, lastValidFields.current.prepaidInterest)) {
        form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(roundToNearestInteger(nextPrepaidInterest)), validateAndTrackOptionsBlock);
      }
      //update discount points
      updateDiscountPoint();
    }

    // 2) on change of homePrice, update downPaymentPercent based on downPayment and origination fee
    if (name === FieldConfig.homePrice.name) {
      var nextDownPaymentPercent = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      var _nextLoanOriginationFee = (lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * 0.5 / 100;
      var nextTitleInsurance = lastValidFields.current[FieldConfig.homePrice.name] * 0.5 / 100;
      var nextSettelementOrEscrowFee = lastValidFields.current[FieldConfig.homePrice.name] * 0.25 / 100;
      var nextHomeownerInsurance = lastValidFields.current[FieldConfig.homePrice.name] * 0.35 / 100;
      var _nextPrepaidInterest = roundToNearestInteger((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      var nextPropertyTaxesAmount = lastValidFields.current[FieldConfig.propertyTaxRate.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name] / 4;
      form.setValue(FieldConfig.downPaymentPercent.name, roundToTheNearestTenth(nextDownPaymentPercent), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(roundToNearestInteger(_nextPrepaidInterest)), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.propertyTaxes.name, getCommaSeparatedNumber(roundToNearestInteger(nextPropertyTaxesAmount)), validateAndTrackOptionsBlock);
      form.setValue(FieldConfig.loanOriginationFee.name, getCommaSeparatedNumber(roundToNearestInteger(_nextLoanOriginationFee)), validateAndTrackOptionsBlock);
      if (!isSameFloat(nextTitleInsurance, !lastValidFields.current.titleInsurance ? 0 : lastValidFields.current.titleInsurance)) {
        form.setValue(FieldConfig.titleInsurance.name, getCommaSeparatedNumber(roundToNearestInteger(nextTitleInsurance)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextSettelementOrEscrowFee, !lastValidFields.current.settelementOrEscrowFee ? 0 : lastValidFields.current.settelementOrEscrowFee)) {
        form.setValue(FieldConfig.settelementOrEscrowFee.name, getCommaSeparatedNumber(roundToNearestInteger(nextSettelementOrEscrowFee)), validateAndTrackOptionsBlock);
      }
      if (!isSameFloat(nextHomeownerInsurance, !lastValidFields.current.homeownerInsurance ? 0 : lastValidFields.current.homeownerInsurance)) {
        form.setValue(FieldConfig.homeownerInsurance.name, getCommaSeparatedNumber(roundToNearestInteger(nextHomeownerInsurance)), validateAndTrackOptionsBlock);
      }
      //update discount points
      updateDiscountPoint();
    }
    // 3) on change of purchaing any discount points, update discount point
    if (name === FieldConfig.purchasingAnyDiscountPoints.name) {
      updateDiscountPoint();
    }
    // 4) on change interestRate, update prepaidInterest
    if (name === FieldConfig.interestRate.name) {
      var _nextPrepaidInterest2 = Math.round((lastValidFields.current[FieldConfig.homePrice.name] - lastValidFields.current[FieldConfig.downPayment.name]) * (lastValidFields.current[FieldConfig.interestRate.name] / 100) / 365 * 15);
      form.setValue(FieldConfig.prepaidInterest.name, getCommaSeparatedNumber(_nextPrepaidInterest2), validateAndTrackOptionsBlock);
    }
  });
}
var DownPaymentField$1 = DownPaymentField;

export { DownPaymentField$1 as default };
